import { type RefObject, useCallback, useRef, useState } from "react";

import type { Size } from "../types.ts";

export type ElementSizeResult = {
  size: Size | undefined;
  /** Read the element itself, e.g. for client coordinates. */
  elementRef: RefObject<HTMLElement | null>;
  /** Attach to the element being measured. */
  setElementRef: (node: HTMLElement | null) => void;
};

/*
  Tracks an element's size, and keeps a ref to the element itself.

  Position is deliberately not tracked: an element can move without resizing, so there is no
  event to observe for it. Anything needing client coordinates should measure the ref at the
  point of use instead.
*/
export const useElementSize = (): ElementSizeResult => {
  const elementRef = useRef<HTMLElement | null>(null);
  const [size, setSize] = useState<Size>();

  const setElementRef = useCallback((node: HTMLElement | null) => {
    elementRef.current = node;
    if (!node) return;

    const observer = new ResizeObserver(() => {
      const { width, height } = node.getBoundingClientRect();
      setSize((current) => (current?.width === width && current.height === height ? current : { width, height }));
    });
    observer.observe(node);

    return () => {
      elementRef.current = null;
      observer.disconnect();
    };
  }, []);

  return { size, elementRef, setElementRef };
};
